// CareersPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, Grid, Button, Card, CardContent, CardActions, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const CareersPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Legal & Company', path: '/company' },
    { name: 'Careers', path: '/company/careers' },
  ];

  const jobOpenings = [
    {
      id: 1,
      title: 'Senior Software Engineer (Frontend - React)',
      location: 'Remote / New York, USA',
      type: 'Full-time',
      description: 'Join our dynamic engineering team to build scalable and intuitive user interfaces for our global trading platform using React, Material-UI, and modern web technologies.',
      requirements: ['5+ years React experience', 'Strong JavaScript/TypeScript skills', 'Experience with RESTful APIs'],
    },
    {
      id: 2,
      title: 'Market Analyst (Scrap Metals)',
      location: 'London, UK',
      type: 'Full-time',
      description: 'Provide in-depth market research and analysis on global scrap metal trends, pricing, and supply chain dynamics to inform business strategy and user insights.',
      requirements: ['3+ years market analysis experience', 'Strong analytical skills', 'Knowledge of commodity markets'],
    },
    {
      id: 3,
      title: 'Logistics Coordinator',
      location: 'Mumbai, India',
      type: 'Full-time',
      description: 'Manage and optimize logistics operations for scrap material shipments, ensuring timely and cost-effective delivery across various regions.',
      requirements: ['2+ years logistics experience', 'Strong organizational skills', 'Knowledge of international shipping'],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Careers
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Join our growing team and be part of a mission to build a sustainable future. We are looking for passionate and talented individuals to help us revolutionize the scrap trading industry.
        </Typography>

        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 4, color: 'primary.dark', textAlign: 'center' }}>
            Current Openings
          </Typography>
          <Grid container spacing={4}>
            {jobOpenings.map((job) => (
              <Grid item xs={12} md={6} key={job.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" fontWeight={600} sx={{ color: 'primary.dark', mb: 1 }}>
                      {job.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {job.location} | {job.type}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                      {job.description}
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ color: 'secondary.main' }}>
                      Key Requirements:
                    </Typography>
                    <List dense disablePadding>
                      {job.requirements.map((req, reqIndex) => (
                        <ListItem key={reqIndex} disablePadding sx={{ py: 0.2 }}>
                          <ListItemText primary={`- ${req}`} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                    <Button variant="contained" sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}>
                      Apply Now
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Don't see a role that fits? Send us your resume at <MuiLink href="mailto:careers@example.com" color="primary.main" underline="hover">careers@example.com</MuiLink>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CareersPage;
